import { CombinedGraphQLErrors } from '@base-app/web2'

export const MAX_VERSION_LENGTH = 32
export const MAX_STORE_URL_LENGTH = 2048

const SEMVER_RE = /^\d+\.\d+\.\d+$/

export function isValidVersionFormat(value: string): boolean {
  return [...value].length <= MAX_VERSION_LENGTH && SEMVER_RE.test(value)
}

export function compareVersions(a: string, b: string): number {
  const segA = a.split('.').map(Number)
  const segB = b.split('.').map(Number)
  for (let i = 0; i < 3; i++) {
    const diff = (segA[i] ?? 0) - (segB[i] ?? 0)
    if (diff !== 0) {
      return diff
    }
  }
  return 0
}

// Mirrors the API: a URL served to apps is visible ASCII only (no backslash, whitespace, control or
// non-ASCII characters), so every parser reads the same host
function isPlainUrlText(value: string): boolean {
  return !/[^\x21-\x7e]/.test(value) && !value.includes('\\')
}

export function isValidAndroidStoreUrl(value: string): boolean {
  if ([...value].length > MAX_STORE_URL_LENGTH || !isPlainUrlText(value)) {
    return false
  }
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname.toLowerCase() === 'play.google.com'
  } catch {
    return false
  }
}

export function isValidIosStoreUrl(value: string): boolean {
  if ([...value].length > MAX_STORE_URL_LENGTH || !isPlainUrlText(value)) {
    return false
  }
  try {
    const url = new URL(value)
    const host = url.hostname.toLowerCase()
    return url.protocol === 'https:' && (host === 'apps.apple.com' || host === 'itunes.apple.com')
  } catch {
    return false
  }
}

// What the form sends for a base URL: the canonical origin + path without trailing slashes, as the
// API stores it (text that doesn't parse is only trimmed; validation refuses it)
export function normalizeBaseUrl(value: string): string {
  const trimmed = value.trim()
  try {
    const url = new URL(trimmed)
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '')
  } catch {
    return trimmed.replace(/\/+$/, '')
  }
}

// The API and image hosts of an application (V-8): https, no credentials, no query or fragment
// (an empty ? or # included), a mobifull.app subdomain. Mirrors the API's validator; the API has
// the final word.
export function isValidBaseUrl(value: string): boolean {
  if (
    [...value].length > MAX_STORE_URL_LENGTH ||
    !isPlainUrlText(value) ||
    value.includes('?') ||
    value.includes('#')
  ) {
    return false
  }
  try {
    const url = new URL(value)
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      url.hostname.toLowerCase().endsWith('.mobifull.app')
    )
  } catch {
    return false
  }
}

const VERSION_GATE_REASONS = [
  'INVALID_VERSION_FORMAT',
  'VERSION_TOO_LONG',
  'VERSION_ORDER_INVALID',
  'STORE_URL_REQUIRED',
  'INVALID_STORE_URL',
  'INVALID_STORE_URL_HOST',
  'STORE_URL_TOO_LONG',
  'INVALID_BASE_URL',
  'INVALID_BASE_URL_HOST',
  'BASE_URL_TOO_LONG',
]

export function extractVersionGateError(
  err: Error,
): { reason: string; field?: string } | undefined {
  if (CombinedGraphQLErrors.is(err)) {
    for (const gqlError of err.errors) {
      const reason = gqlError.extensions?.reason
      if (typeof reason === 'string' && VERSION_GATE_REASONS.includes(reason)) {
        const field =
          typeof gqlError.extensions?.field === 'string' ? gqlError.extensions.field : undefined
        return { reason, field }
      }
    }
  }
  return undefined
}
